import React, { useState, useEffect } from 'react';
import { DollarSign, Plus } from 'lucide-react';
import type { FinanceStats, Transaction } from './FinanceTypes';
import { TransactionForm } from './TransactionForm';
import { TransactionList } from './TransactionList';
import { formatCurrency } from '../../utils/formatters';
import { useTransactions } from '../../hooks/useTransactions';

interface DeleteConfirmationProps {
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteConfirmation({ onConfirm, onCancel }: DeleteConfirmationProps) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center">
      <div className="bg-[#2A2B2E] rounded-3xl p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-semibold mb-4">Delete Transaction</h3>
        <p className="text-gray-400 mb-6">Are you sure you want to delete this transaction? This action cannot be undone.</p>
        <div className="flex gap-4">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-black/20 rounded-xl hover:bg-black/30 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export function FinanceOverview() {
  const [showForm, setShowForm] = useState<'invoice' | 'expense' | null>(null);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const { transactions, loading, error: transactionError, addTransaction, deleteTransaction } = useTransactions();
  const [formError, setFormError] = useState<string | null>(null);
  const [deletingTransaction, setDeletingTransaction] = useState<string | null>(null);

  // Calculate financial stats from transactions
  const calculateStats = () => {
    const revenue = transactions
      .filter(t => t.type === 'invoice')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const expenses = transactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const profit = revenue - expenses;
    
    return [
      { label: 'Revenue', value: formatCurrency(revenue) },
      { label: 'Expenses', value: formatCurrency(expenses) },
      { label: 'Profit', value: formatCurrency(profit) }
    ];
  };

  const handleAddTransaction = async (transaction: Omit<Transaction, 'id'>) => {
    try {
      setFormError(null);
      if (editingTransaction) {
        // TODO: Implement updateTransaction
        await updateTransaction(editingTransaction.id, transaction);
        setEditingTransaction(null);
      } else {
        await addTransaction(transaction);
      }
      setShowForm(null);
    } catch (err) {
      console.error('Error adding transaction:', err);
      setFormError('Failed to add transaction. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTransaction(id);
      setDeletingTransaction(null);
    } catch (err) {
      console.error('Error deleting transaction:', err);
      setFormError('Failed to delete transaction. Please try again.');
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--accent-color)]/20 flex items-center justify-center text-[var(--accent-color)]">
            <DollarSign className="w-5 h-5" />
          </div>
          <h3 className="font-semibold">Finance Overview</h3>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-6">
        {calculateStats().map((stat, i) => (
          <div key={i} className="text-center">
            {stat.value.startsWith('-') ? (
              <div className="text-lg font-semibold text-red-500">{stat.value}</div>
            ) : (
              <div className="text-lg font-semibold">{stat.value}</div>
            )}
            <div className="text-xs text-gray-400">{stat.label}</div>
          </div>
        ))}
      </div>
      
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => setShowForm('invoice')}
          className="flex items-center gap-2 px-4 py-2 bg-[#bbdde5] rounded-xl text-black hover:opacity-90 transition-opacity text-sm"
        >
          <Plus className="w-4 h-4" />
          Add Invoice
        </button>
        <button
          onClick={() => setShowForm('expense')}
          className="flex items-center gap-2 px-4 py-2 bg-[#bbdde5] rounded-xl text-black hover:opacity-90 transition-opacity text-sm"
        >
          <Plus className="w-4 h-4" />
          Add Expense
        </button>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold mb-4">Recent Transactions</h4>
        {loading ? (
          <div className="text-center text-gray-400">Loading transactions...</div>
        ) : transactionError ? (
          <div className="flex items-center justify-center gap-2 text-red-500">
            <AlertCircle className="w-5 h-5" />
            <p>Error loading transactions</p>
          </div>
        ) : (
        <TransactionList 
          transactions={transactions.slice(0, 5)} 
          onEdit={(transaction) => {
            setEditingTransaction(transaction);
            setShowForm(transaction.type);
          }}
          onDelete={(id) => setDeletingTransaction(id)}
        />
        )}
      </div>
      
      {formError && (
        <div className="fixed bottom-4 right-4 flex items-center gap-2 p-4 bg-red-500/10 rounded-xl text-red-500">
          <AlertCircle className="w-5 h-5" />
          <p>{formError}</p>
          <button
            onClick={() => setFormError(null)}
            className="p-1 hover:bg-black/20 rounded-full ml-2"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {showForm && (
        <TransactionForm
          type={showForm}
          transaction={editingTransaction}
          onSubmit={handleAddTransaction}
          onClose={() => {
            setShowForm(null);
            setEditingTransaction(null);
          }}
        />
      )}

      {deletingTransaction && (
        <DeleteConfirmation
          onConfirm={() => handleDelete(deletingTransaction)}
          onCancel={() => setDeletingTransaction(null)}
        />
      )}
    </div>
  );
}